/**
 * What a replay attempt actually meant, and what to do about it.
 *
 * Pulled out of the mutation because the decision is the part that is easy to get wrong and
 * worth pinning: the endpoint answers **200 with `replayed: false`** when the message still
 * cannot be parsed. That is a normal, expected outcome — the gap has not been fixed yet — and
 * reading the HTTP status alone reports it as a success, leaves the operator believing the
 * message was filed, and hides the reason it was not.
 */

const STILL_UNPARSEABLE =
  "The message still could not be parsed.";

/** Whether the list has to be refetched, because the row is no longer a dead letter. */
export const describeReplayOutcome = (result) => {
  if (result?.replayed) {
    return {
      tone: "success",
      // Careful wording: the message is queued, not filed. It is consumed by the next
      // inbound poll — 15 minutes by default — so saying it has arrived would be a lie the
      // operator only discovers by going to look for it.
      message:
        "Message queued for re-processing. It will be picked up by the next inbound pass.",
      shouldRefresh: true,
    };
  }

  return {
    tone: "error",
    message: result?.failureReason
      ? `Still could not be parsed: ${result.failureReason}`
      : STILL_UNPARSEABLE,
    // The row stays where it is. Refreshing would suggest something changed.
    shouldRefresh: false,
  };
};

/**
 * A 404 means it is not a dead letter of this tenant any more — a stale id, or someone
 * replayed it first. That is "already gone", not an error to alarm anyone with.
 */
export const isAlreadyGone = (error) => error?.response?.status === 404;
